import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import { Modal, Form, Row, Col } from 'antd';

import Button from 'components/Button/Button';
import FormInput from 'components/FormInput/FormInput';
import Description from 'components/Description/Description';
import FormSelection from 'components/FormSelection/FormSelection';
import FormAutoComplete from 'components/FormAutoComplete/FormAutoComplete';
import GoogleMap from 'components/VanillaGoogleMap/VanillaGoogleMap';
import Location from 'components/Location/Location';
import { searchProperties } from 'utils/apis/properties';
import { getPropertyTypes } from 'utils/apis/constants';

const StyledForm = styled(Form)`
padding: 8% 4% !important;
width: 100%;

@media (min-width: 992px) {
  padding: 4% 8% !important;
}
`;
class PropertyInfo extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    form: PropTypes.object,
    onPropertySave: PropTypes.func,
    visible: PropTypes.bool,
    defaultValue: PropTypes.object,
    selectedBuildings: PropTypes.array
  };

  static defaultProps = {
    defaultValue: {},
    onPropertySave: () => {},
    visible: false,
    selectedBuildings: []
  };

  state = {
    disable: false,
    zoom: 6,
    latitude: undefined,
    longitude: undefined,
    properties: [],
    propertySelections: [],
    propertyTypeSelections: [],
    isHostSearchProperty: false
  };

  async componentDidMount() {
    return this.fetchPropertyTypes();
  }

  fetchPropertyTypes = () => {
    return getPropertyTypes().then(res => {
      const propertyTypeSelections = Object.values(res.data).map(propertyType => ({
        key: propertyType.code,
        value: propertyType.label
      }));
      this.setState({
        propertyTypeSelections
      });
    });
  };

  handleCancel = e => {
    this.resetState();
  };

  handleSubmit = e => {
    const { form, onPropertySave } = this.props;
    const { propertyName, longitude, latitude, isHostSearchProperty } = this.state;
    e.preventDefault();
    form.validateFields((err, values) => {
      if (!err) {
        onPropertySave({ ...values, propertyName, longitude, latitude, isHostSearchProperty });
        this.resetState();
      }
    });
  };

  resetState = () => {
    const { form, onModalClose } = this.props;
    form.resetFields();
    this.setState({
      properties: [],
      propertySelections: [],
      disable: false,
      zoom: 6,
      latitude: undefined,
      longitude: undefined,
      isHostSearchProperty: false
    });
    onModalClose();
  };

  handleOnMapCoordinateChange = (lat, lng) => {
    this.setState({
      latitude: lat,
      longitude: lng,
      zoom: 16
    });
  };

  handleOnMapMarkerMove = newLocation => {
    this.setState({
      latitude: newLocation.lat,
      longitude: newLocation.lng
    });
  };

  handleOnSearchChange = async search => {
    const { selectedBuildings } = this.props;

    const properties = await searchProperties(search, selectedBuildings);
    this.setState({
      properties,
      propertyName: search,
      propertySelections: properties.map(property => {
        return {
          key: property._id,
          value: property.name
        };
      }),
      isHostSearchProperty: false,
      disable: false
    });
  };

  handleOnSelectProperty = select => {
    const { properties } = this.state;
    const { form } = this.props;
    const selectedProperty = properties.filter(property => property._id === select)[0];
    form.setFieldsValue({
      propertyAddress: selectedProperty.streetAddress,
      propertyCity: selectedProperty.city,
      propertyZipCode: selectedProperty.zipcode,
      propertyCountry: selectedProperty.country,
      propertyState: selectedProperty.state,
      totalNoOfUnits: selectedProperty.totalNoOfUnits,
      propertyType: selectedProperty.type
    });
    this.setState({
      zoom: 16,
      longitude: selectedProperty.longitude,
      latitude: selectedProperty.latitude,
      disable: true,
      propertyName: selectedProperty.name,
      isHostSearchProperty: true
    });
  };

  render() {
    const { form, visible, defaultValue } = this.props;
    const { longitude, latitude, zoom, disable, propertySelections, propertyTypeSelections } = this.state;
    return (
      <Modal width="1000px" maskClosable={false} visible={visible} onCancel={this.handleCancel} footer={null} destroyOnClose>
        <Row gutter={48}>
          <StyledForm onSubmit={this.handleSubmit}>
            <Col span={24} md={24} xl={12}>
              <Row>
                <Description type="large">Basic Information</Description>
              </Row>
              <Row>
                <FormAutoComplete
                  style={{ width: '100%' }}
                  form={form}
                  name="propertyId"
                  label="Property Name"
                  requiredErrorMessage="Please enter your property name"
                  placeholder="Enter the name of the property"
                  size="large"
                  defaultValue={defaultValue.name}
                  selections={propertySelections}
                  onSearch={this.handleOnSearchChange ? this.handleOnSearchChange : () => {}}
                  onSelect={this.handleOnSelectProperty ? this.handleOnSelectProperty : () => {}}
                />
              </Row>
              <Row gutter={16}>
                <Col span={24} md={12}>
                  <FormSelection
                    form={form}
                    name="propertyType"
                    label="Property Type"
                    defaultValue={defaultValue.type}
                    selections={propertyTypeSelections}
                    size="large"
                    requiredErrorMessage="Please select a property type."
                    shouldDisable={disable}
                    placeholder="Choose the most suitable type for your property"
                  />
                </Col>
                <Col span={24} md={12}>
                  <FormInput
                    form={form}
                    name="totalNoOfUnits"
                    label="Total No. of Units"
                    size="large"
                    defaultValue={defaultValue.totalNoOfUnits}
                    requiredErrorMessage="Please enter number of units available in your property"
                    placeholder="Total units owned by you in this property"
                  />
                </Col>
              </Row>
              <Row>
                <Description type="large">Location Details</Description>
              </Row>
              <Location data={defaultValue} form={form} onCoordinateChange={this.handleOnMapCoordinateChange} isDisabled={disable} />
            </Col>
            <Col span={24} md={24} xl={12}>
              <Row>
                <Description type="regular">Move the pin to confirm your property location</Description>
              </Row>
              <GoogleMap
                googleMapURL="https://maps.googleapis.com/maps/api/js?key=AIzaSyAHBWhCJOD2ow59B91tQWXL_lunDkacQXE&v=3.exp&libraries=geometry,drawing,places"
                loadingElement={<div style={{ height: `50%` }} />}
                containerElement={<div style={{ height: `490px` }} />}
                mapElement={<div style={{ height: `100%` }} />}
                setCoords={this.handleOnMapMarkerMove}
                lng={longitude || defaultValue.longitude}
                lat={latitude || defaultValue.latitude}
                zoom={defaultValue.longitude ? 16 : zoom}
              />
              <Row type="flex" justify="space-between">
                <Col span={8} md={6} xl={6}>
                  <Button size="large" style={{ width: '100%', marginTop: '24px' }} type="secondary" onClick={this.handleCancel}>
                    Back
                  </Button>
                </Col>
                <Col span={8} md={6} xl={6}>
                  <Button size="large" style={{ width: '100%', marginTop: '24px' }} type="primary" htmlType="submit">
                    Next
                  </Button>
                </Col>
              </Row>
            </Col>
          </StyledForm>
        </Row>
      </Modal>
    );
  }
}

export default Form.create()(PropertyInfo);

